import { useLocStore } from "../wsLocalization";
import { LocContext } from "../wsLocalization/LocContext";
import { useComputed } from "./useComputed";

export const getCallingCode = (locale: string): number => {
  if (locales[locale]) return locales[locale].callingCode;

  throw new Error("Locale not implemented: " + locale);
};

// https://support.crowdin.com/api/language-codes/
export enum CvpLocale {
  /** Dutch 31 */
  "nl" = "nl",

  /** French 33 */
  "fr" = "fr",

  /** Spanish 34 */
  "es-ES" = "es-ES",

  /** Italian 39 */
  "it" = "it",

  /** English 44 */
  "en" = "en",

  /** Danish 45 */
  "da" = "da",

  /** Swedish 46 */
  "sv-SE" = "sv-SE",

  /** Norwegian Bokmal 47 */
  "nb" = "nb",

  /** Polish 48 */
  "pl" = "pl",

  /** German 49 */
  "de" = "de",

  /** Somali 252 */
  "so" = "so",

  /** Portuguese 351 */
  "pt-PT" = "pt-PT",

  /** Lithuanian 370 */
  "lt" = "lt",

  /** Latvian 371 */
  "lv" = "lv",

  /** Slovenian 386 */
  "sl" = "sl",

  /** Slovak 421 */
  "sk" = "sk",
}

export interface LocaleObj {
  callingCode: number;
  cvpLocale: CvpLocale;
  localeNameNorwegian: string;
}

// https://www.nationsonline.org/oneworld/international-calling-codes.htm
export const locales: { [key: string]: LocaleObj } = {
  nl: {
    callingCode: 31,
    cvpLocale: CvpLocale.nl,
    localeNameNorwegian: "Nederlandsk",
  },
  fr: {
    callingCode: 33,
    cvpLocale: CvpLocale.fr,
    localeNameNorwegian: "Fransk",
  },
  "es-ES": {
    callingCode: 34,
    cvpLocale: CvpLocale["es-ES"],
    localeNameNorwegian: "Spansk",
  },
  it: {
    callingCode: 39,
    cvpLocale: CvpLocale.it,
    localeNameNorwegian: "Italiensk",
  },
  en: {
    callingCode: 44,
    cvpLocale: CvpLocale.en,
    localeNameNorwegian: "Engelsk",
  },
  da: {
    callingCode: 45,
    cvpLocale: CvpLocale.da,
    localeNameNorwegian: "Dansk",
  },
  "sv-SE": {
    callingCode: 46,
    cvpLocale: CvpLocale["sv-SE"],
    localeNameNorwegian: "Svensk",
  },
  nb: {
    callingCode: 47,
    cvpLocale: CvpLocale.nb,
    localeNameNorwegian: "Norsk",
  },
  pl: {
    callingCode: 48,
    cvpLocale: CvpLocale.pl,
    localeNameNorwegian: "Polsk",
  },
  de: {
    callingCode: 49,
    cvpLocale: CvpLocale.de,
    localeNameNorwegian: "Tysk",
  },
  so: {
    callingCode: 252,
    cvpLocale: CvpLocale.so,
    localeNameNorwegian: "Somalisk",
  },
  "pt-PT": {
    callingCode: 351,
    cvpLocale: CvpLocale["pt-PT"],
    localeNameNorwegian: "Portugisisk",
  },
  lt: {
    callingCode: 370,
    cvpLocale: CvpLocale.lt,
    localeNameNorwegian: "Litauisk",
  },
  lv: {
    callingCode: 371,
    cvpLocale: CvpLocale.lv,
    localeNameNorwegian: "Latvisk",
  },
  sl: {
    callingCode: 386,
    cvpLocale: CvpLocale.sl,
    localeNameNorwegian: "Slovensk",
  },
  sk: {
    callingCode: 421,
    cvpLocale: CvpLocale.sk,
    localeNameNorwegian: "Slovakisk",
  },
};

export const useLocaleList = () => {
  const L = useLocStore(LocContext.ADMIN);

  return useComputed(() =>
    Object.values(locales).map((locale) => ({
      label:
        L.id("locale_" + locale.cvpLocale).loc(locale.localeNameNorwegian) +
        ` [${locale.cvpLocale}]`,
      value: locale.cvpLocale,
    }))
  );
};
